import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { getMarketDetails, placeBet } from '../utils/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const MarketDetail = () => {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [market, setMarket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showBetModal, setShowBetModal] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [betAmount, setBetAmount] = useState('');
  const [isPlacingBet, setIsPlacingBet] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'chart', label: 'Price Chart', icon: 'BarChart3' },
    { id: 'trades', label: 'Recent Trades', icon: 'Activity' },
    { id: 'about', label: 'About', icon: 'BookOpen' }
  ];

  useEffect(() => {
    const fetchMarket = async () => {
      if (!marketId) {
        setError('Market ID not provided');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await getMarketDetails(marketId);
        if (response?.success) {
          setMarket(response?.data);
          if (response?.data?.outcomeTokens?.length > 0) {
            setSelectedOutcome(response?.data?.outcomeTokens?.[0]?.name);
          }
        } else {
          setError(response?.error || 'Failed to load market details');
        }
      } catch (err) {
        console.error('Failed to fetch market details:', err);
        setError('Unable to load market details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarket();
  }, [marketId]);

  const handlePlaceBet = async () => {
    if (!market || !selectedOutcome || !betAmount) return;

    setIsPlacingBet(true);
    try {
      const outcome = market?.outcomeTokens?.find(t => t?.name === selectedOutcome);
      const response = await placeBet(market?.id, selectedOutcome, parseFloat(betAmount), outcome?.price);

      if (response?.success) {
        setShowBetModal(false);
        setBetAmount('');
        // You could show a success notification here
      }
    } catch (error) {
      console.error('Error placing bet:', error);
    } finally {
      setIsPlacingBet(false);
    }
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) return `$${(volume / 1000000)?.toFixed(1)}M`;
    if (volume >= 1000) return `$${(volume / 1000)?.toFixed(0)}K`;
    return `$${volume}`;
  };

  const getOutcomeColor = (price) => {
    if (price > 0.7) return 'text-success';
    if (price > 0.5) return 'text-warning-400';
    return 'text-error';
  };

  const getDaysUntilEnd = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Ended';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const getEstimatedShares = () => {
    if (!betAmount || !selectedOutcome || !market) return 0;
    const outcome = market?.outcomeTokens?.find(t => t?.name === selectedOutcome);
    return Math.floor(parseFloat(betAmount) / (outcome?.price || 1));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-text-secondary">Loading market details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !market) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="px-6 py-6">
          <div className="bg-error-900 bg-opacity-20 border border-error-600 text-error-300 p-6 rounded-trading flex items-center backdrop-blur-sm">
            <Icon name="AlertTriangle" size={24} className="mr-4 flex-shrink-0" />
            <div>
              <h3 className="font-body-semibold mb-2">Unable to Load Market</h3>
              <p className="text-sm text-error-400 mb-4">{error}</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => window.location?.reload()}
                  className="bg-gradient-primary text-white px-4 py-2 rounded-trading text-sm hover:opacity-80 transition-opacity glow-primary"
                >
                  Retry
                </button>
                <button
                  onClick={() => navigate('/markets')}
                  className="bg-surface-hover text-text-primary px-4 py-2 rounded-trading text-sm hover:bg-border-hover border border-border transition-colors"
                >
                  Back to Markets
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="px-6 py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/markets')}
          className="flex items-center space-x-2 text-text-secondary hover:text-text-primary mb-6 transition-colors duration-trading-fast"
        >
          <Icon name="ArrowLeft" size={20} />
          <span>Back to Markets</span>
        </button>

        {/* Market Header */}
        <div className="bg-surface border border-border rounded-trading shadow-trading-card p-8 mb-8 backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            <div className="flex-1">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                  <Icon name="TrendingUp" size={24} color="white" />
                </div>
                <div className="flex items-center space-x-3">
                  <span className="bg-primary-900 text-primary-300 px-3 py-1 rounded text-sm font-body-medium">
                    {market?.category}
                  </span>
                  <span className="text-sm text-text-secondary flex items-center">
                    <Icon name="Clock" size={14} className="mr-1" />
                    Ends {getDaysUntilEnd(market?.endDate)}
                  </span>
                </div>
              </div>
              
              <h1 className="text-3xl font-heading-bold text-text-primary mb-4 leading-tight">
                {market?.title}
              </h1>
              
              <p className="text-lg text-text-secondary leading-relaxed">
                {market?.description}
              </p>
            </div>

            <div className="flex flex-col lg:items-end space-y-4">
              <div className="text-right">
                <p className="text-sm text-text-secondary mb-1">Total Volume</p>
                <p className="text-2xl font-data text-text-primary">
                  {formatVolume(market?.totalVolume)}
                </p>
              </div>
              
              <button
                onClick={() => setShowBetModal(true)}
                className="bg-gradient-primary text-white px-8 py-3 rounded-trading font-body-semibold text-lg hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
              >
                Place Bet
              </button>
            </div>
          </div>
        </div>

        {/* Current Prices */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {market?.outcomeTokens?.map((token, index) => (
            <div key={index} className="bg-surface border border-border rounded-trading shadow-trading-card p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-body-semibold text-text-primary">{token?.name}</h3>
                <Icon name={index === 0 ? 'TrendingUp' : 'TrendingDown'} size={24} className={getOutcomeColor(token?.price)} />
              </div>
              
              <div className="flex items-baseline space-x-2 mb-2">
                <span className={`text-4xl font-data ${getOutcomeColor(token?.price)}`}>
                  ${token?.price?.toFixed(2)}
                </span>
                <span className="text-sm text-text-secondary">per share</span>
              </div>
              
              <div className="flex justify-between text-sm text-text-secondary">
                <span>Volume: {formatVolume(token?.volume)}</span>
                <span>Shares: {token?.shares?.toLocaleString()}</span>
              </div>
              
              <button
                onClick={() => {
                  setSelectedOutcome(token?.name);
                  setShowBetModal(true);
                }}
                className="w-full mt-4 bg-surface-hover hover:bg-border-hover border border-border text-text-primary py-2 rounded-trading font-body-medium transition-colors duration-trading-fast"
              >
                Buy {token?.name}
              </button>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-surface border border-border rounded-trading shadow-trading-card backdrop-blur-sm">
          <div className="flex items-center space-x-1 p-4 border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                  activeTab === tab?.id
                    ? 'bg-primary-900 text-primary-300 border border-primary-800 glow-primary' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-body-semibold text-text-primary mb-3">Market Stats</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-surface-hover rounded-trading p-4">
                      <p className="text-xs text-text-secondary mb-1">Total Volume</p>
                      <p className="font-data text-lg text-text-primary">{formatVolume(market?.totalVolume)}</p>
                    </div>
                    <div className="bg-surface-hover rounded-trading p-4">
                      <p className="text-xs text-text-secondary mb-1">Liquidity</p>
                      <p className="font-data text-lg text-text-primary">{formatVolume(market?.liquidity)}</p>
                    </div>
                    <div className="bg-surface-hover rounded-trading p-4">
                      <p className="text-xs text-text-secondary mb-1">End Date</p>
                      <p className="font-data text-lg text-text-primary">{new Date(market?.endDate)?.toLocaleDateString()}</p>
                    </div>
                    <div className="bg-surface-hover rounded-trading p-4">
                      <p className="text-xs text-text-secondary mb-1">Status</p>
                      <p className="font-data text-lg text-success">{market?.status}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'chart' && (
              <div>
                <h3 className="text-lg font-body-semibold text-text-primary mb-4">Price History</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={market?.priceHistory || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#94A3B8"
                        fontSize={12}
                        tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
                      />
                      <YAxis stroke="#94A3B8" fontSize={12} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#1A2332',
                          border: '1px solid #2D3748',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="yesPrice" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        name="Yes Price"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="noPrice" 
                        stroke="#EF4444" 
                        strokeWidth={2}
                        name="No Price"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {activeTab === 'trades' && (
              <div>
                <h3 className="text-lg font-body-semibold text-text-primary mb-4">Recent Trades</h3>
                <div className="space-y-2">
                  {market?.recentTrades?.slice(0, 10)?.map((trade, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-surface-hover rounded-trading">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${trade?.type === 'buy' ? 'bg-success' : 'bg-error'}`}></div>
                        <span className="font-body-medium text-text-primary">{trade?.outcome}</span>
                        <span className="text-sm text-text-secondary">{trade?.shares} shares</span>
                      </div>
                      <div className="text-right">
                        <p className="font-data text-text-primary">${trade?.price}</p>
                        <p className="text-xs text-text-secondary">
                          {new Date(trade?.timestamp)?.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'about' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-body-semibold text-text-primary mb-3">About This Market</h3>
                  <p className="text-text-secondary leading-relaxed">
                    {market?.description}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-body-semibold text-text-primary mb-2">Resolution Criteria</h4>
                  <p className="text-sm text-text-secondary">
                    This market will resolve based on official data and announcements. 
                    The resolution source will be clearly documented when the market closes.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bet Modal */}
        {showBetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-modal flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-surface border border-border rounded-trading shadow-trading-modal max-w-md w-full p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-heading-semibold text-text-primary">Place Bet</h3>
                <button
                  onClick={() => setShowBetModal(false)}
                  className="p-2 rounded-trading hover:bg-surface-hover transition-colors duration-trading-fast"
                >
                  <Icon name="X" size={20} className="text-text-secondary" />
                </button>
              </div>

              {/* Outcome Selection */}
              <div className="mb-6">
                <label className="block text-sm font-body-medium text-text-primary mb-3">Select Outcome</label>
                <div className="space-y-2">
                  {market?.outcomeTokens?.map((token, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedOutcome(token?.name)}
                      className={`w-full flex items-center justify-between p-4 rounded-trading border transition-all duration-trading-fast ${
                        selectedOutcome === token?.name
                          ? 'border-primary bg-primary-900 bg-opacity-20' :'border-border hover:border-border-hover hover:bg-surface-hover'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          selectedOutcome === token?.name ? 'border-primary bg-primary' : 'border-border'
                        }`}></div>
                        <span className="font-body-medium text-text-primary">{token?.name}</span>
                      </div>
                      <span className={`font-data ${getOutcomeColor(token?.price)}`}>
                        ${token?.price?.toFixed(2)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bet Amount */}
              <div className="mb-6">
                <label className="block text-sm font-body-medium text-text-primary mb-2">Bet Amount (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary">$</span>
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e?.target?.value)}
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    className="w-full pl-8 pr-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
                  />
                </div>
              </div>

              {/* Bet Summary */}
              {betAmount && (
                <div className="mb-6 p-4 bg-surface-hover rounded-trading">
                  <h4 className="font-body-semibold text-text-primary mb-2">Bet Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Estimated Shares:</span>
                      <span className="text-text-primary font-data">{getEstimatedShares()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Max Return:</span>
                      <span className="text-success font-data">${getEstimatedShares()?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowBetModal(false)}
                  disabled={isPlacingBet}
                  className="flex-1 bg-surface-hover text-text-primary py-3 rounded-trading font-body-semibold hover:bg-border-hover border border-border transition-colors duration-trading-fast disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlaceBet}
                  disabled={isPlacingBet || !betAmount || !selectedOutcome}
                  className="flex-1 bg-gradient-primary text-white py-3 rounded-trading font-body-semibold hover:opacity-90 transition-opacity duration-trading-fast disabled:opacity-50 disabled:cursor-not-allowed glow-primary"
                >
                  {isPlacingBet ? 'Placing...' : 'Place Bet'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketDetail;